import { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartCtx = createContext(null);
const KEY = "am_cart_v1";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(KEY) || "[]");
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(items));
  }, [items]);

  const add = (product, { size, color, quantity = 1 } = {}) => {
    setItems((prev) => {
      const key = `${product.id}|${size || ""}|${color || ""}`;
      const idx = prev.findIndex(
        (i) => `${i.product_id}|${i.size || ""}|${i.color || ""}` === key
      );
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = { ...copy[idx], quantity: copy[idx].quantity + quantity };
        return copy;
      }
      const price = product.sale_price || product.price;
      const image =
        (product.images || []).find((i) => i.is_primary)?.path ||
        (product.images || [])[0]?.path ||
        null;
      return [
        ...prev,
        {
          product_id: product.id,
          name: product.name,
          price,
          image,
          size: size || null,
          color: color || null,
          quantity,
          stock: product.stock,
        },
      ];
    });
    setOpen(true);
  };

  const updateQty = (key, qty) => {
    setItems((prev) =>
      prev
        .map((i) =>
          `${i.product_id}|${i.size || ""}|${i.color || ""}` === key
            ? { ...i, quantity: Math.max(1, qty) }
            : i
        )
        .filter((i) => i.quantity > 0)
    );
  };

  const remove = (key) => {
    setItems((prev) =>
      prev.filter((i) => `${i.product_id}|${i.size || ""}|${i.color || ""}` !== key)
    );
  };

  const clear = () => setItems([]);

  const subtotal = useMemo(
    () => items.reduce((s, i) => s + i.price * i.quantity, 0),
    [items]
  );
  const count = useMemo(
    () => items.reduce((s, i) => s + i.quantity, 0),
    [items]
  );

  return (
    <CartCtx.Provider
      value={{ items, add, updateQty, remove, clear, subtotal, count, open, setOpen }}
    >
      {children}
    </CartCtx.Provider>
  );
}

export const useCart = () => useContext(CartCtx);
